import Phaser from "phaser";
import { Button } from "../ui/Button";
import { HealthBar } from "../ui/HealthBar";
import { COLORS, HUD_HEIGHT, LAYOUT, TEXT_STYLES } from "../constants";
import type { RunState } from "../state/runState";
import { tagTextForAutomation } from "../automation/automationTags";
import { formatRunInfo } from "../screens/uiHelpers";
import { PrimitiveIcon } from "../avatars/PrimitiveIcon";

export const RUN_HUD_SCENE_KEY = "RUN_HUD";

export const RUN_CHANGED_EVENT = "run:changed";
export const RUN_QUIT_EVENT = "run:quit";

export type RunBannerType = "victory" | "defeat";

export type RunBannerConfig = {
  type: RunBannerType;
  subtitle?: string;
};

export const RUN_BANNER_SHOW_EVENT = "run:banner:show";
export const RUN_BANNER_HIDE_EVENT = "run:banner:hide";

const BANNER_HEIGHT = 140;

const skillIdToIconId = (skillId: string): string => {
  // Map skill IDs to icon IDs
  if (skillId === "attack") return "attack";
  if (skillId === "slash") return "slash";
  if (skillId === "defend") return "defend";
  if (skillId === "bloodPact") return "health";
  return "default";
};

const perkIdToIconId = (perkId: string): string => {
  // Map perk IDs to icon IDs
  if (perkId === "perk_hp") return "hp";
  if (perkId === "perk_atk") return "atk";
  if (perkId === "perk_lifesteal") return "lifesteal";
  if (perkId === "perk_armor") return "armor";
  if (perkId === "perk_thorns") return "thorns";
  if (perkId === "perk_adrenaline") return "adrenaline";
  if (perkId === "perk_rage") return "rage";
  return "default";
};

export class RunHudScene extends Phaser.Scene {
  private root!: Phaser.GameObjects.Container;
  private bg!: Phaser.GameObjects.Rectangle;
  private shadow!: Phaser.GameObjects.Rectangle;

  private bannerContainer!: Phaser.GameObjects.Container;
  private bannerTitle!: Phaser.GameObjects.Text;
  private bannerSubtitle!: Phaser.GameObjects.Text;
  private bannerRestY = HUD_HEIGHT;
  private bannerTween?: Phaser.Tweens.Tween;

  // Authoritative banner state (intent)
  private currentBannerType: RunBannerType | null = null;
  private currentBannerSubtitle: string | null = null;
  private isBannerVisible = false;

  private runStateText!: Phaser.GameObjects.Text;
  private hpBar!: HealthBar;
  private atkIcon!: PrimitiveIcon;
  private atkValueText!: Phaser.GameObjects.Text;
  private armorIcon!: PrimitiveIcon;
  private armorValueText!: Phaser.GameObjects.Text;
  private skillsContainer!: Phaser.GameObjects.Container;
  private perksContainer!: Phaser.GameObjects.Container;
  private runInfoAutomationText!: Phaser.GameObjects.Text;
  private playerHpAutomationText!: Phaser.GameObjects.Text;
  private playerAtkAutomationText!: Phaser.GameObjects.Text;
  private playerArmorAutomationText!: Phaser.GameObjects.Text;

  private quitButton!: Button;

  private run?: RunState;
  private unsubscribeRunChanged?: () => void;
  private unsubscribeBannerEvents?: () => void;

  constructor() {
    super(RUN_HUD_SCENE_KEY);
  }

  create() {
    const { width } = this.scale;

    this.root = this.add.container(0, 0);
    this.root.setScrollFactor(0);

    // Subtle vignette / top bar background
    this.bg = this.add.rectangle(0, 0, width, HUD_HEIGHT, COLORS.background, 0.55).setOrigin(0, 0);
    this.bg.setStrokeStyle(1, COLORS.panelStroke, 0.45);
    this.bg.disableInteractive();

    // Tiny shadow to separate HUD from world/UI below
    this.shadow = this.add.rectangle(0, HUD_HEIGHT, width, 10, 0x000000, 0.22).setOrigin(0, 0);
    this.shadow.disableInteractive();

    const padX = Math.max(16, LAYOUT.screenPadding - 8);
    const topY = 12;

    // Right column (run state + skills/perks)
    const rightColumnWidth = 240;
    const rightX = width - padX;
    const rightColumnLeftX = width - padX - rightColumnWidth;

    // Left column (HP + stats)
    const leftX = padX;
    const leftWidth = Math.max(140, rightColumnLeftX - leftX - 10);

    // Quit button (top-right)
    const quitWidth = 130;
    const quitHeight = 48;
    this.quitButton = new Button(this, width - padX - quitWidth, topY, {
      width: quitWidth,
      height: quitHeight,
      label: "Quit Run",
      automationName: "quit-run",
      fillColor: COLORS.buttonFill,
      strokeColor: COLORS.buttonStroke,
      onClick: () => this.game.events.emit(RUN_QUIT_EVENT),
    });
    this.quitButton.setDepth(10);

    // Run state (beneath quit)
    this.runStateText = this.add
      .text(rightX, topY + quitHeight + 8, "Floor —\nEncounters —", {
        ...TEXT_STYLES.body,
        fontSize: "18px",
        color: COLORS.textPrimary,
        align: "right",
      })
      .setOrigin(1, 0);

    // HP bar
    this.hpBar = new HealthBar(this, {
      width: Math.max(240, leftWidth),
      height: 36,
      borderColor: COLORS.panelStroke,
      backgroundColor: 0x0b1020,
      fillColor: Phaser.Display.Color.HexStringToColor(COLORS.healText).color,
      textStyle: {
        ...TEXT_STYLES.body,
        fontSize: "20px",
        color: "#ffffff",
      },
    });
    this.hpBar.setPosition(leftX, topY);

    // Player stats row (icons like Encounter player info card)
    const statsY = topY + 48;
    const iconSize = 24;
    const iconScale = iconSize / 32;
    const iconSpacing = 10;
    const groupSpacing = 22;
    let currentX = leftX;

    this.atkIcon = new PrimitiveIcon(this, "attack", { scale: iconScale });
    this.atkIcon.setPosition(currentX + iconSize / 2, statsY + iconSize / 2);
    currentX += iconSize + iconSpacing;
    this.atkValueText = this.add
      .text(currentX, statsY, "—", {
        ...TEXT_STYLES.body,
        fontSize: "20px",
        color: COLORS.textPrimary,
      })
      .setOrigin(0, 0);
    currentX += 50 + groupSpacing;

    this.armorIcon = new PrimitiveIcon(this, "armor", { scale: iconScale });
    this.armorIcon.setPosition(currentX + iconSize / 2, statsY + iconSize / 2);
    currentX += iconSize + iconSpacing;
    this.armorValueText = this.add
      .text(currentX, statsY, "—", {
        ...TEXT_STYLES.body,
        fontSize: "20px",
        color: COLORS.textPrimary,
      })
      .setOrigin(0, 0);

    // Skills / perks on left side below stats
    this.skillsContainer = this.add.container(leftX, statsY + 38);
    this.perksContainer = this.add.container(leftX, statsY + 68);

    // Hidden automation-only run-info snapshot (replaces the old on-screen run info card).
    // Keep alpha > 0 to avoid some renderers skipping bounds.
    this.runInfoAutomationText = this.add
      .text(0, 0, "", { ...TEXT_STYLES.body, fontSize: "12px" })
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setAlpha(0.001);
    tagTextForAutomation(this.runInfoAutomationText, "run-info");

    // Hidden automation-only player stats (keeps Playwright stable after removing the encounter player info card).
    this.playerHpAutomationText = this.add
      .text(0, 0, "", { ...TEXT_STYLES.body, fontSize: "12px" })
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setAlpha(0.001);
    tagTextForAutomation(this.playerHpAutomationText, "player-hp");

    this.playerAtkAutomationText = this.add
      .text(0, 0, "", { ...TEXT_STYLES.body, fontSize: "12px" })
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setAlpha(0.001);
    tagTextForAutomation(this.playerAtkAutomationText, "player-atk");

    this.playerArmorAutomationText = this.add
      .text(0, 0, "", { ...TEXT_STYLES.body, fontSize: "12px" })
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setAlpha(0.001);
    tagTextForAutomation(this.playerArmorAutomationText, "player-armor");

    this.root.add([
      this.bg,
      this.shadow,
      this.quitButton,
      this.runStateText,
      this.hpBar.getRoot(),
      this.atkIcon.getRoot(),
      this.atkValueText,
      this.armorIcon.getRoot(),
      this.armorValueText,
      this.skillsContainer,
      this.perksContainer,
      this.runInfoAutomationText,
      this.playerHpAutomationText,
      this.playerAtkAutomationText,
      this.playerArmorAutomationText,
    ]);

    this.createRunBanner();

    // Listen for run updates.
    const onRunChanged = (run: RunState) => {
      this.run = run;
      this.renderFromRun();
    };
    this.game.events.on(RUN_CHANGED_EVENT, onRunChanged);
    this.unsubscribeRunChanged = () => this.game.events.off(RUN_CHANGED_EVENT, onRunChanged);

    const onBannerShow = (config: RunBannerConfig) => this.showRunBanner(config);
    const onBannerHide = () => this.hideRunBanner();
    this.game.events.on(RUN_BANNER_SHOW_EVENT, onBannerShow);
    this.game.events.on(RUN_BANNER_HIDE_EVENT, onBannerHide);
    this.unsubscribeBannerEvents = () => {
      this.game.events.off(RUN_BANNER_SHOW_EVENT, onBannerShow);
      this.game.events.off(RUN_BANNER_HIDE_EVENT, onBannerHide);
    };

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.unsubscribeRunChanged?.();
      this.unsubscribeRunChanged = undefined;
      this.unsubscribeBannerEvents?.();
      this.unsubscribeBannerEvents = undefined;
    });
    this.events.once(Phaser.Scenes.Events.DESTROY, () => {
      this.unsubscribeRunChanged?.();
      this.unsubscribeRunChanged = undefined;
      this.unsubscribeBannerEvents?.();
      this.unsubscribeBannerEvents = undefined;
    });
  }

  private createRunBanner() {
    const { width } = this.scale;

    this.bannerRestY = HUD_HEIGHT;
    this.bannerContainer = this.add.container(0, this.bannerRestY - BANNER_HEIGHT);
    this.bannerContainer.setAlpha(0);
    this.bannerContainer.setVisible(false);
    this.bannerContainer.setScrollFactor(0);
    this.bannerContainer.setDepth(1000);

    const bannerWidth = width - LAYOUT.screenPadding * 2;
    const bannerBg = this.add
      .rectangle(
        LAYOUT.screenPadding,
        LAYOUT.screenPadding,
        bannerWidth,
        BANNER_HEIGHT,
        COLORS.panelFill
      )
      .setOrigin(0, 0);
    bannerBg.setStrokeStyle(2, COLORS.panelStroke);
    bannerBg.disableInteractive();

    this.bannerTitle = this.add
      .text(width / 2, LAYOUT.screenPadding + 22, "", TEXT_STYLES.title)
      .setOrigin(0.5, 0);
    this.bannerSubtitle = this.add
      .text(width / 2, LAYOUT.screenPadding + 70, "", TEXT_STYLES.subtitle)
      .setOrigin(0.5, 0);

    this.bannerContainer.add([bannerBg, this.bannerTitle, this.bannerSubtitle]);
    this.root.add(this.bannerContainer);
  }

  private showRunBanner({ type, subtitle }: RunBannerConfig) {
    // Guard: If the same banner is already visible, do nothing
    if (
      this.isBannerVisible &&
      this.currentBannerType === type &&
      this.currentBannerSubtitle === (subtitle ?? null)
    ) {
      return;
    }

    this.currentBannerType = type;
    this.currentBannerSubtitle = subtitle ?? null;
    this.isBannerVisible = true;

    const title = type === "victory" ? "Victory" : "Defeat";
    this.bannerTitle.setText(title);
    this.bannerSubtitle.setText(subtitle ?? "");
    this.bannerSubtitle.setVisible(Boolean(subtitle));

    this.bannerTween?.stop();
    this.bannerContainer.setVisible(true);
    this.bannerContainer.setAlpha(1);
    this.bannerContainer.setY(this.bannerRestY - BANNER_HEIGHT);

    this.bannerTween = this.tweens.add({
      targets: this.bannerContainer,
      y: this.bannerRestY,
      alpha: 1,
      duration: 240,
      ease: "Sine.easeOut",
    });
  }

  private hideRunBanner() {
    if (!this.isBannerVisible) {
      return;
    }

    this.isBannerVisible = false;
    this.currentBannerType = null;
    this.currentBannerSubtitle = null;

    this.bannerTween?.stop();
    this.bannerTween = this.tweens.add({
      targets: this.bannerContainer,
      y: this.bannerRestY - BANNER_HEIGHT,
      alpha: 0,
      duration: 200,
      ease: "Sine.easeIn",
      onComplete: () => {
        this.bannerContainer.setVisible(false);
      },
    });
  }

  private renderSkillIcons(skills: Record<string, number>) {
    // Clear existing icons
    this.skillsContainer.removeAll(true);

    const entries = Object.entries(skills).sort((a, b) => a[0].localeCompare(b[0]));
    if (entries.length === 0) {
      // Show "no skills" placeholder
      const placeholder = this.add
        .text(0, 0, "—", {
          ...TEXT_STYLES.body,
          fontSize: "18px",
          color: COLORS.textSecondary,
        })
        .setOrigin(0, 0);
      this.skillsContainer.add(placeholder);
      return;
    }

    const iconSize = 28;
    const iconScale = iconSize / 32;
    const iconSpacing = 10;
    let currentX = 0;

    for (const [skillId, level] of entries) {
      const iconId = skillIdToIconId(skillId);
      const icon = new PrimitiveIcon(this, iconId, { scale: iconScale });
      icon.setPosition(currentX + iconSize / 2, iconSize / 2);
      this.skillsContainer.add(icon.getRoot());

      // Add level indicator if level > 1
      if (level > 1) {
        const levelText = this.add
          .text(currentX + iconSize - 3, iconSize - 3, String(level), {
            ...TEXT_STYLES.body,
            fontSize: "14px",
            color: COLORS.textPrimary,
            fontStyle: "bold",
          })
          .setOrigin(1, 1)
          .setStroke("#000000", 3);
        this.skillsContainer.add(levelText);
      }

      currentX += iconSize + iconSpacing;
    }
  }

  private renderPerkIcons(perkIds: string[]) {
    // Clear existing icons
    this.perksContainer.removeAll(true);

    if (perkIds.length === 0) {
      // Show "no perks" placeholder
      const placeholder = this.add
        .text(0, 0, "—", {
          ...TEXT_STYLES.body,
          fontSize: "18px",
          color: COLORS.textSecondary,
        })
        .setOrigin(0, 0);
      this.perksContainer.add(placeholder);
      return;
    }

    // Count duplicate perks
    const perkCounts = new Map<string, number>();
    for (const perkId of perkIds) {
      perkCounts.set(perkId, (perkCounts.get(perkId) ?? 0) + 1);
    }

    const iconSize = 28;
    const iconScale = iconSize / 32;
    const iconSpacing = 10;
    let currentX = 0;

    // Sort by perk ID for consistent ordering
    const uniquePerks = Array.from(perkCounts.entries()).sort((a, b) => a[0].localeCompare(b[0]));

    for (const [perkId, count] of uniquePerks) {
      const iconId = perkIdToIconId(perkId);
      const icon = new PrimitiveIcon(this, iconId, { scale: iconScale });
      icon.setPosition(currentX + iconSize / 2, iconSize / 2);
      this.perksContainer.add(icon.getRoot());

      // Add count indicator if count > 1
      if (count > 1) {
        const countText = this.add
          .text(currentX + iconSize - 3, iconSize - 3, String(count), {
            ...TEXT_STYLES.body,
            fontSize: "14px",
            color: COLORS.textPrimary,
            fontStyle: "bold",
          })
          .setOrigin(1, 1)
          .setStroke("#000000", 3);
        this.perksContainer.add(countText);
      }

      currentX += iconSize + iconSpacing;
    }
  }

  private renderFromRun() {
    const run = this.run;
    if (!run) return;

    this.hpBar.setHP(run.player.hp, run.player.maxHp);

    const totalEncounters = run.encountersCleared + run.encountersRemaining;
    const encounters =
      totalEncounters > 0
        ? `${run.encountersCleared}/${totalEncounters}`
        : `${run.encountersRemaining}`;
    this.runStateText.setText(`Floor ${run.floor}\nEncounters ${encounters}`);

    this.atkValueText.setText(String(run.player.atk));
    this.armorValueText.setText(String(run.player.armor));

    // Render skill and perk icons
    this.renderSkillIcons(run.player.skills);
    this.renderPerkIcons(run.player.perks);

    this.runInfoAutomationText.setText(formatRunInfo(run));
    this.playerHpAutomationText.setText(`${run.player.hp}/${run.player.maxHp}`);
    this.playerAtkAutomationText.setText(String(run.player.atk));
    this.playerArmorAutomationText.setText(String(run.player.armor));
  }
}
